import { useEffect, useRef, type ReactNode } from 'react'
import type { Organisation, User } from '../api/types'
import { BrandIcon, ChevronDown, SignOut, Swap, Users } from './Icons'

/**
 * The bar across the top of every signed-in page.
 *
 * Where you are on the left, as a breadcrumb back to the events; what you can do on this page in
 * the middle-right; who you are on the far right, as one menu. The occasional actions -- members,
 * switching club, signing out -- used to be three loose links beside a faint callsign; gathered
 * behind the person they belong to, they stop competing with the page's own actions.
 */
export function AppHeader({
  user,
  organisation,
  crumbs,
  actions,
  onMembers,
  onSwitchOrganisation,
  onSignOut,
}: {
  user: User
  organisation: Organisation
  /** After the organisation: e.g. the event's name. The last one is the current page. */
  crumbs?: ReactNode
  actions?: ReactNode
  onMembers?: () => void
  onSwitchOrganisation: () => void
  onSignOut: () => void
}) {
  const menu = useRef<HTMLDetailsElement>(null)

  // A menu closes when you click elsewhere or press Escape, as every menu does.
  useEffect(() => {
    const close = (event: MouseEvent | KeyboardEvent) => {
      const details = menu.current
      if (!details?.open) return
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !details.contains(event.target as Node)) {
        details.open = false
      }
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', close)
    }
  }, [])

  const run = (action?: () => void) => () => {
    if (menu.current) menu.current.open = false
    action?.()
  }

  return (
    <header className="app-header">
      <div className="brand">
        <span className="brand-mark" aria-hidden>
          <BrandIcon />
        </span>
        <nav className="breadcrumb" aria-label="Breadcrumb">
          {crumbs ? (
            <>
              {crumbs}
            </>
          ) : (
            <span className="current">{organisation.name}</span>
          )}
        </nav>
      </div>

      <div className="header-actions">
        {actions}
        <details className="menu" ref={menu}>
          <summary aria-label={`Account: ${user.callsign}`}>
            <span className="avatar" aria-hidden>
              {user.callsign.slice(0, 1).toUpperCase()}
            </span>
            <span className="hide-narrow">{user.callsign}</span>
            <ChevronDown />
          </summary>
          <div className="menu-items" role="menu">
            <div className="menu-note">
              {organisation.name}
              {organisation.myRole && ` · ${roleName(organisation.myRole)}`}
            </div>
            {onMembers && (
              <button role="menuitem" className="with-icon" onClick={run(onMembers)}>
                <Users /> Members
              </button>
            )}
            <button role="menuitem" className="with-icon" onClick={run(onSwitchOrganisation)}>
              <Swap /> Switch organisation
            </button>
            <button role="menuitem" className="with-icon" onClick={run(onSignOut)}>
              <SignOut /> Sign out
            </button>
          </div>
        </details>
      </div>
    </header>
  )
}

function roleName(role: NonNullable<Organisation['myRole']>): string {
  return { OWNER: 'Owner', MANAGER: 'Manager', STAFF: 'Staff' }[role] ?? role
}
